import { z } from 'zod';
// ============= Dependency Graph Types =============
/**
 * Package manager types.
 */
export const PackageManager = z.enum([
    'npm',
    'yarn',
    'pnpm',
    'bun',
    'pip',
    'poetry',
    'cargo',
    'go-mod',
    'maven',
    'gradle',
    'composer',
    'unknown',
]);
/**
 * Dependency types.
 */
export const DependencyType = z.enum([
    'production',
    'development',
    'peer',
    'optional',
    'build',
    'workspace',
]);
/**
 * Package status types.
 */
export const PackageStatus = z.enum([
    'healthy',
    'outdated',
    'deprecated',
    'vulnerable',
    'unknown',
]);
/**
 * Package node in dependency graph.
 */
export const PackageNodeSchema = z.object({
    id: z.string(),
    name: z.string(),
    version: z.string().optional(),
    description: z.string().optional(),
    isLocal: z.boolean().default(false),
    isWorkspace: z.boolean().default(false),
    path: z.string().optional(),
    packageManager: PackageManager.optional(),
    status: PackageStatus.optional(),
    license: z.string().optional(),
    homepage: z.string().optional(),
    repository: z.string().optional(),
});
/**
 * Dependency edge in the graph.
 */
export const DependencyEdgeSchema = z.object({
    id: z.string(),
    from: z.string(),
    to: z.string(),
    type: DependencyType,
    versionRange: z.string().optional(),
    isCircular: z.boolean().default(false),
});
/**
 * Workspace configuration.
 */
export const WorkspaceConfigSchema = z.object({
    root: z.string(),
    packages: z.array(z.string()),
    packageManager: PackageManager,
    isMonorepo: z.boolean().default(false),
});
/**
 * Complete dependency graph diagram data.
 */
export const DependencyDiagramDataSchema = z.object({
    metadata: z.object({
        title: z.string(),
        description: z.string().optional(),
        packageManager: PackageManager,
        rootPackage: z.string().optional(),
        totalPackages: z.number().optional(),
        generatedAt: z.date().optional(),
        sourceFiles: z.array(z.string()).optional(),
    }),
    packages: z.array(PackageNodeSchema),
    dependencies: z.array(DependencyEdgeSchema),
    workspace: WorkspaceConfigSchema.optional(),
});
// ============= Helper Functions =============
/**
 * Get color for dependency type.
 */
export function getDependencyTypeColor(type) {
    switch (type) {
        case 'production':
            return '#22c55e'; // Green
        case 'development':
            return '#3b82f6'; // Blue
        case 'peer':
            return '#f59e0b'; // Amber
        case 'optional':
            return '#8b5cf6'; // Purple
        case 'build':
            return '#06b6d4'; // Cyan
        case 'workspace':
            return '#ec4899'; // Pink
        default:
            return '#64748b'; // Slate
    }
}
/**
 * Get color for package status.
 */
export function getPackageStatusColor(status) {
    switch (status) {
        case 'healthy':
            return '#22c55e'; // Green
        case 'outdated':
            return '#f59e0b'; // Amber
        case 'deprecated':
            return '#ef4444'; // Red
        case 'vulnerable':
            return '#dc2626'; // Dark Red
        case 'unknown':
        default:
            return '#64748b'; // Slate
    }
}
/**
 * Get icon/emoji for package manager.
 */
export function getPackageManagerIcon(manager) {
    switch (manager) {
        case 'npm':
            return 'npm';
        case 'yarn':
            return 'yarn';
        case 'pnpm':
            return 'pnpm';
        case 'bun':
            return 'bun';
        case 'pip':
        case 'poetry':
            return 'python';
        case 'cargo':
            return 'rust';
        case 'go-mod':
            return 'go';
        case 'maven':
        case 'gradle':
            return 'java';
        case 'composer':
            return 'php';
        default:
            return 'package';
    }
}
/**
 * Detect circular dependencies in the graph.
 */
export function detectCircularDependencies(packages, dependencies) {
    const cycles = [];
    const visited = new Set();
    const recursionStack = new Set();
    const path = [];
    // Build adjacency list
    const adjacency = new Map();
    for (const pkg of packages) {
        adjacency.set(pkg.id, []);
    }
    for (const dep of dependencies) {
        const existing = adjacency.get(dep.from) || [];
        existing.push(dep.to);
        adjacency.set(dep.from, existing);
    }
    function dfs(nodeId) {
        visited.add(nodeId);
        recursionStack.add(nodeId);
        path.push(nodeId);
        const neighbors = adjacency.get(nodeId) || [];
        for (const neighbor of neighbors) {
            if (!visited.has(neighbor)) {
                dfs(neighbor);
            }
            else if (recursionStack.has(neighbor)) {
                // Found a cycle
                const cycleStart = path.indexOf(neighbor);
                if (cycleStart !== -1) {
                    cycles.push([...path.slice(cycleStart), neighbor]);
                }
            }
        }
        path.pop();
        recursionStack.delete(nodeId);
    }
    for (const pkg of packages) {
        if (!visited.has(pkg.id)) {
            dfs(pkg.id);
        }
    }
    return cycles;
}
/**
 * Get all transitive dependencies for a package.
 */
export function getTransitiveDependencies(packageId, dependencies, maxDepth = 10) {
    const result = new Set();
    const queue = [{ id: packageId, depth: 0 }];
    while (queue.length > 0) {
        const current = queue.shift();
        if (current.depth >= maxDepth)
            continue;
        const directDeps = dependencies.filter(d => d.from === current.id);
        for (const dep of directDeps) {
            if (!result.has(dep.to)) {
                result.add(dep.to);
                queue.push({ id: dep.to, depth: current.depth + 1 });
            }
        }
    }
    return result;
}
/**
 * Filter to show only workspace/local packages.
 */
export function filterToWorkspacePackages(data) {
    const workspacePackageIds = new Set(data.packages.filter(p => p.isLocal || p.isWorkspace).map(p => p.id));
    return {
        ...data,
        packages: data.packages.filter(p => workspacePackageIds.has(p.id)),
        dependencies: data.dependencies.filter(d => workspacePackageIds.has(d.from) && workspacePackageIds.has(d.to)),
    };
}
/**
 * Convert DependencyDiagramData to UnifiedDiagramData.
 */
export function dependencyToUnifiedDiagram(data) {
    const nodes = [];
    const edges = [];
    // Calculate positions using a simple force-directed-like layout
    const packageCount = data.packages.length;
    const cols = Math.ceil(Math.sqrt(packageCount));
    data.packages.forEach((pkg, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);
        nodes.push({
            id: pkg.id,
            type: pkg.isLocal || pkg.isWorkspace ? 'component' : 'external',
            label: pkg.name,
            description: pkg.version ? `v${pkg.version}` : undefined,
            position: {
                x: col * 200,
                y: row * 150,
            },
            data: {
                version: pkg.version,
                isLocal: pkg.isLocal,
                isWorkspace: pkg.isWorkspace,
                status: pkg.status,
                license: pkg.license,
            },
        });
    });
    for (const dep of data.dependencies) {
        edges.push({
            id: dep.id,
            source: dep.from,
            target: dep.to,
            type: dep.isCircular ? 'bidirectional' : 'dependency',
            animated: dep.isCircular, // Animate circular dependencies for visibility
            label: dep.type,
            data: {
                dependencyType: dep.type,
                versionRange: dep.versionRange,
                isCircular: dep.isCircular,
            },
        });
    }
    return {
        metadata: {
            title: data.metadata.title,
            description: data.metadata.description,
            diagramType: 'dependencyGraph',
            category: 'architecture',
            layoutDirection: 'LR',
            layoutAlgorithm: 'force',
            sourceFiles: data.metadata.sourceFiles,
            generatedAt: data.metadata.generatedAt,
            generatedBy: 'regex',
        },
        nodes,
        edges,
    };
}
//# sourceMappingURL=dependency.js.map